import React, { useEffect, useState } from 'react';
import { Modal, Typography, Descriptions, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';

const { Title } = Typography;

const ParticipantDetailsModal = ({ open, onClose, participantData, host }) => {
    const customFieldsConfig = participantData?.config?.CustomFields || [];
    const participant = participantData?.participant?.participant;
    const allValues = participant?.participantCustomFieldValues?.participantCustomFieldValue || [];
    const [resolvedLabels, setResolvedLabels] = useState({});
    const [loading, setLoading] = useState(false);

    const groupedFields = customFieldsConfig.map(group => {
        const fieldsInGroup = group.fields
            .map(field => {
                const match = allValues.find(val => Number(val.participantCustomFieldID) === field.id);
                return match
                    ? { name: field.name, value: match.value, fieldId: field.id }
                    : null;
            })
            .filter(Boolean);

        return {
            groupName: group.groupName,
            fields: fieldsInGroup,
        };
    }).filter(group => group.fields.length > 0);

    useEffect(() => {
        if (!open || !host) return;

        const fetchLabels = async () => {
            setLoading(true);
            try {
                const tokenRes = await axios.get(`/regasus/token?host=${host}`);
                const token = tokenRes.data?.access_token;

                const fieldIds = customFieldsConfig
                    .flatMap(group => group.fields.map(field => field.id))
                    .filter(Boolean);

                const result = {};

                await Promise.all(fieldIds.map(async (id) => {
                    try {
                        const res = await axios.get(`/regasus/participant-custom-fields/${id}`, {
                            params: { token, host }
                        });

                        const valueList = res.data?.participantCustomField?.valueList?.participantCustomFieldListValue || [];

                        if (!result[id]) result[id] = {};

                        for (const item of valueList) {
                            const label = item.label?.find(l => l.lang === 'en')?.value
                                || item.label?.[0]?.value
                                || item.id;
                            result[id][item.id] = label;
                        }
                    } catch (e) {
                        console.warn(`❌ No se pudo resolver el campo ${id}:`, e.message);
                    }
                }));

                setResolvedLabels(result);
            } catch (err) {
                console.error('❌ Error al obtener token o valores personalizados:', err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchLabels();
    }, [open ]);

    const renderResolvedValue = (field) => {
        const labelMap = resolvedLabels[field.fieldId];

        if (!labelMap) return field.value;

        if (field.value.includes(',')) {
            return field.value
                .split(',')
                .map(v => labelMap[v] || v)
                .join(', ');
        }

        return labelMap[field.value] || field.value;
    };

    return (
        <Modal
            title={
                <span>
                    <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    Details
                </span>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={600}
        >
            {loading ? (
                <Spin tip="Loading field labels..." />
            ) : (
                groupedFields.map(group => (
                    <div key={group.groupName} style={{ marginBottom: 24 }}>
                        <Title level={5} style={{ marginBottom: 12 }}>{group.groupName}</Title>
                        <Descriptions
                            bordered
                            column={1}
                            size="small"
                            style={{ borderRadius: 6 }}
                        >
                            {group.fields.map((field, index) => (
                                <Descriptions.Item label={field.name} key={index}>
                                    {renderResolvedValue(field)}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </div>
                ))
            )}
        </Modal>
    );
};

export default ParticipantDetailsModal;